import React, { useState, useEffect } from 'react';
import {
    Card,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Box,
} from '@mui/material';
import Styles from './studentVerification.module.scss';

export const StudentRegistration = ({ onSubmit, loading }) => {
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({
        email: false,
        name: false,
        institution_name: false,
        student_class: false,
        student_division: false,
        roll_number: false,
    });

    const [formData, setFormData] = useState({
        email: '',
        name: '',
        institution_name: '',
        student_class: '',
        student_division: '',
        roll_number: '',
    });

    const [isFormValid, setIsFormValid] = useState(false);

    const validateField = (name, value) => {
        if (!value) return `${name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} is required`;

        switch (name) {
            case 'email':
                return !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value)
                    ? 'Please enter a valid email address'
                    : '';
            case 'name':
                return !/^[A-Za-z\s]+$/.test(value)
                    ? 'Name can only contain letters and spaces'
                    : '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) newErrors[field] = error;
        });
        return newErrors;
    };

    useEffect(() => {
        // Check if all fields have values and there are no errors
        const fieldsFilled = Object.values(formData).every(value => value.trim() !== '');
        const currentErrors = validateForm();
        const hasErrors = Object.keys(currentErrors).length > 0;
        setIsFormValid(fieldsFilled && !hasErrors);
    }, [formData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Validate field on change if it's been touched
        if (touched[name]) {
            const error = validateField(name, value);
            setErrors(prev => ({
                ...prev,
                [name]: error
            }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Validate on blur
        const error = validateField(name, formData[name]);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    return (
        <Card className={Styles.registrationCard}>
            <div className={Styles.registrationHeader}>
                <Typography variant="h4" className={Styles.headerName}>
                    Student Registration
                </Typography>
                <Typography variant='subtitle1' sx={{ color: '#666' }}>
                    Please provide your details to complete the registration process and proceed to the assessment
                </Typography>
            </div>


            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit(e, formData);
                }}
                className={Styles.registrationForm}
            >
                <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                    placeholder="your.email@school.edu"
                />

                <TextField
                    required
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                    placeholder="John Smith"
                />

                <TextField
                    required
                    fullWidth
                    label="Institution Name"
                    name="institution_name"
                    value={formData.institution_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={touched.institution_name && !!errors.institution_name}
                    helperText={touched.institution_name && errors.institution_name}
                    placeholder="Your School Name"
                />

                <div className={Styles.classDivisionContainer}>
                    <TextField
                        required
                        fullWidth
                        label="Class"
                        name="student_class"
                        value={formData.student_class}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={touched.student_class && !!errors.student_class}
                        helperText={touched.student_class && errors.student_class}
                        placeholder="10th"
                    />

                    <TextField
                        required
                        fullWidth
                        label="Division"
                        name="student_division"
                        value={formData.student_division}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={touched.student_division && !!errors.student_division}
                        helperText={touched.student_division && errors.student_division}
                        placeholder="A"
                    />
                </div>

                <TextField
                    required
                    fullWidth
                    label="Roll Number"
                    name="roll_number"
                    value={formData.roll_number}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={touched.roll_number && !!errors.roll_number}
                    helperText={touched.roll_number && errors.roll_number}
                    placeholder="42"
                />

                <div className={Styles.submitButtonContainer}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isFormValid || loading}
                    >
                        {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} color="inherit" />
                                Submitting...
                            </Box>
                        ) : 'Complete Registration'}
                    </Button>
                </div>
            </form>
        </Card >
    );
};